"use client";

import {
  GTMSpotlightCarouselTestResult,
  GTMSpotlightCarouselData,
} from "@/monarch/rules/gtmSpotlightCarousel/types";
import NewProductCarousel from ".";
import MonarchResolver from "@/monarch/MonarchResolver";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import useSWR from "swr";
import ProductCard from "@/components/ProductCard";
import {
  FormatSubType,
  DEFAULT_USE_IN_VIEW_OPTIONS,
} from "@/constants/tagular/main";
import type { ProductSummary } from "@/common/productSummary/types";
import { useResponsiveGroupSize } from "./useResponsiveGroupSize";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import { getHighestLevel } from "../NewProductCard/utils/getHighestLevel";
import { useInView } from "react-intersection-observer";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { useEffect, useRef, useMemo } from "react";
import useTranslations from "@/i18n/useTranslations";

type FetcherParams = {
  uuids?: string[];
  createdWithinDays?: number;
  goLiveWithinDays?: number;
  modifiedWithinDays?: number;
  partnerKeys?: string[];
  attributes?: string[];
  limit?: number;
};

const isNavigableProductSummary = (productSummary: ProductSummary) => {
  return Boolean(
    productSummary.productSlug &&
      productSummary.isActive !== false &&
      productSummary.showInAlgoliaSearchResults !== false,
  );
};

const fetcher = async ([url, params]: [string, FetcherParams]) => {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
  if (!response.ok) throw new Error("Failed to fetch products");
  return response.json() as Promise<ProductSummary[]>;
};

type GTMSpotlightCarouselContentProps = Pick<
  GTMSpotlightCarouselData,
  | "productUuiDs"
  | "products"
  | "header"
  | "subheader"
  | "dynamicHeader"
  | "dynamicSubheader"
  | "rotationEnabled"
  | "rotationTiming"
  | "backgroundColor"
  | "dayRange"
  | "dateField"
  | "partnerKeys"
  | "attributes"
  | "cta"
>;

function getDateFilterParam(
  dayRange: number | undefined,
  dateField: GTMSpotlightCarouselData["dateField"],
): Pick<
  FetcherParams,
  "createdWithinDays" | "goLiveWithinDays" | "modifiedWithinDays"
> {
  if (!dayRange || dayRange <= 0) return {};
  switch (dateField) {
    case "goLiveDate":
      return { goLiveWithinDays: dayRange };
    case "modifiedDate":
      return { modifiedWithinDays: dayRange };
    default:
      return { createdWithinDays: dayRange };
  }
}

function GTMSpotlightCarouselContent({
  products,
  header,
  subheader,
  rotationEnabled,
  rotationTiming,
  backgroundColor,
  dayRange,
  dateField,
  partnerKeys,
  attributes,
  cta,
}: GTMSpotlightCarouselContentProps) {
  const tHomepage = useTranslations("Homepage");
  const uuids = products?.map((product) => product.uuid).filter(Boolean);

  const hasFilters =
    (dayRange !== undefined && dayRange > 0) ||
    (partnerKeys && partnerKeys.length > 0) ||
    (attributes && attributes.length > 0);

  const shouldFetch = uuids?.length || hasFilters;
  const limit = 24;

  const fetchParams: FetcherParams = {
    uuids: uuids?.length ? uuids : undefined,
    ...getDateFilterParam(dayRange, dateField),
    partnerKeys,
    attributes,
    limit,
  };

  const { data: productSummaries, isLoading } = useSWR(
    shouldFetch ? ["/api/product-summaries", fetchParams] : null,
    fetcher,
  );

  const groupSize = useResponsiveGroupSize();
  const isMobile = groupSize === 1;

  const webElement = useMemo(
    () => ({
      elementType: "COMPONENT" as const,
      htmlId: "gtm-spotlight-carousel",
      location: "gtm-spotlight-carousel",
      position: "1",
      name: "gtm-spotlight-carousel",
      text: header || "New Products",
    }),
    [header],
  );

  const { elementViewed } = useElementEvents({ webElement });
  const hasViewedRef = useRef(false);
  const { ref: inViewRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  useEffect(() => {
    if (inView && !hasViewedRef.current) {
      hasViewedRef.current = true;
      elementViewed();
    }
  }, [inView, elementViewed]);

  if (isLoading || (!products?.length && !hasFilters)) {
    return null;
  }

  const liveProductSummaries =
    productSummaries?.filter(isNavigableProductSummary) ?? [];

  const displayProducts = isMobile
    ? liveProductSummaries.slice(0, 4)
    : liveProductSummaries;

  const sections = displayProducts?.map((product, index) => {
    return (
      <ProductCard
        key={product.productUuid}
        name={product.productName}
        href={`/${product.productSlug}`}
        partnerKey={product.partnerKeys}
        partnerFullName={
          Array.isArray(product.partnerName)
            ? product.partnerName[0]
            : product.partnerName
        }
        imageUrl={product.productImageUrl}
        partnerLogoUrl={product.partnerLogoUrl}
        type={product.productType}
        subjects={product.attributes}
        formatSubtype={FormatSubType.Grid}
        productSource="carousel"
        resultLevel="product"
        location="gtm-spotlight-carousel"
        index={index}
        objectId={product.objectID}
        uuid={product.productUuid}
        parentTitle={
          !header || header === "New Products"
            ? tHomepage("newProducts")
            : header
        }
        activeRunKey={product.activeRunKey}
        weeksToComplete={product.weeksToComplete ?? undefined}
        childCoursesCount={
          product.childCoursesCount ?? product.courseCount ?? undefined
        }
        level={getHighestLevel(product.level)}
        isNew={true}
        className="w-[266px]"
      />
    );
  });

  return (
    <>
      {liveProductSummaries.length > 2 ? (
        <div ref={inViewRef}>
          <NewProductCarousel
            sections={sections ?? []}
            pageTitle={header}
            subheader={subheader}
            useAutoplay={rotationEnabled}
            autoplayDelay={rotationTiming}
            backgroundColor={backgroundColor}
            cta={cta}
          />
        </div>
      ) : null}
    </>
  );
}

type GTMSpotlightCarouselWrapperProps = {
  subject?: string;
  partnerKey?: string;
};

function GTMMonarchTestWrapper({
  subject,
  partnerKey,
}: GTMSpotlightCarouselWrapperProps = {}) {
  return (
    <MonarchResolver<GTMSpotlightCarouselTestResult>
      experimentKey="useGTMSpotlightCarousel"
      Loader={null}
      variants={{
        control: <></>,
        test: (
          <MonarchDataConsumer<GTMSpotlightCarouselTestResult>
            experimentKey="useGTMSpotlightCarousel"
            render={(testResult) => {
              const { data } = testResult ?? {};
              const dynamicHeader = data?.dynamicHeader;
              const dynamicSubheader = data?.dynamicSubheader;

              if (!data?.visible) {
                return null;
              }

              const replaceHeaderVariable = (
                header: string,
                variable: string,
              ) => {
                return header.replace("{{variable}}", variable);
              };

              const variableWord = subject || partnerKey;

              const header =
                variableWord && dynamicHeader?.withVariable
                  ? replaceHeaderVariable(
                      dynamicHeader?.withVariable,
                      variableWord,
                    )
                  : dynamicHeader?.static;

              const subheader =
                variableWord && dynamicSubheader?.withVariable
                  ? replaceHeaderVariable(
                      dynamicSubheader?.withVariable,
                      variableWord,
                    )
                  : dynamicSubheader?.static;

              return (
                <GTMSpotlightCarouselContent
                  productUuiDs={data.productUuiDs}
                  products={data.products}
                  header={header}
                  subheader={subheader}
                  rotationEnabled={data.rotationEnabled}
                  rotationTiming={data.rotationTiming}
                  backgroundColor={data.backgroundColor}
                  dayRange={data.dayRange}
                  dateField={data.dateField}
                  partnerKeys={partnerKey ? [partnerKey] : undefined}
                  attributes={subject ? [subject] : undefined}
                  cta={data.cta}
                />
              );
            }}
          />
        ),
      }}
    />
  );
}

export default function GTMSpotlightCarouselWrapper({
  subject,
  partnerKey,
}: GTMSpotlightCarouselWrapperProps = {}) {
  return (
    <FeatureFlagComponentResolverClient
      flagKey="NEXT_PUBLIC_FEATURE_GTM_AUTOMATION_MODULE"
      variants={{
        test: (
          <GTMMonarchTestWrapper subject={subject} partnerKey={partnerKey} />
        ),
        legacy: <></>,
      }}
      fallback="legacy"
    />
  );
}
